import React, { useState, useEffect, memo } from "react";
import {
    TextField,
    IconButton,
    Grid,
    Stack,
    Autocomplete,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import { toast } from "react-hot-toast";
import AddBoxIcon from "@mui/icons-material/AddBox";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { useSelector } from "react-redux";
import configServ from "../../services/config";
import CheckAndReturn from "../../utility/CheckAndReturn";
import Constants from "../../utility/Constants";
import UploadListImage from "../Upload/UploadListImage";
import CurrencyList from "../List/CurrencyList";

const initialRow = {
    account: "",
    product: "",
    costmaster: "",
    location: "",
    currency: "",
    amount: "",
    image: "",
    pdf: "",
};

//FUNCTION
const BudgetItem1 = ({
    voucherDetails,
    editVoucherDetails,
    setError,
    reset,
}) => {
    //#region code
    const { company_id } = useSelector((state) => state.GlobalVariables);
    const [rows, setRows] = useState([{ ...initialRow }]);
    const [accountList, setAccountList] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [locations, setLocations] = useState([]);
    const [products, setProducts] = useState([]);
    const [costMasterList, setCostMasterList] = useState([]);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        setRows([{ ...initialRow }]);
    }, [reset]);

    const fetchEditData = async () => {
        const detail = editVoucherDetails.map((item) => ({
            account: item.account,
            product: item.product,
            costmaster: item.costmaster,
            location: item.location,
            currency: item.currency,
            amount: item.amount,
            image: item.document,
            fileType: item.fileType,
        }));
        setRows(detail);
    };

    useEffect(() => {
        if (editVoucherDetails) {
            fetchEditData();
        }
    }, [editVoucherDetails]);

    const fetchAccountList = async () => {
        try {
            const res = await configServ.getAllAccountMasters({
                company_id: company_id,
            });
            if (res.status === 200) {
                const result = res.data.filter(x => x.status === 1);
                setAccountList(result);
            }
            else {
                setAccountList([]);
            }
        }
        catch (error) {
            console.log(error);
        }
    };

    const fetchSupplierList = async () => {
        try {
            const dataToSend = {
                company_id: company_id,
            };
            const res = await configServ.getSupplier(dataToSend);
            if (res.status === 200) {
                const result = res.data.filter((x) => x.status === 1);
                setSuppliers(result);
            }
        } catch (error) {
            console.log(error);
        }
    };

    const fetchLocation = async () => {
        try {
            const res = await configServ.getLocation({
                company_id: company_id,
            });
            if (res.length > 0) {
                const locationResponse = res.filter((x) => x.status === 1);
                const result = locationResponse.sort((a, b) =>
                    a.location_name.localeCompare(b.location_name)
                );
                setLocations(result);
            }
            else {
                setLocations([]);
            }
        }
        catch (error) {
            console.log(error);
        }
    };

    const fetchItemList = async () => {
        try {
            const dataToSend = {
                company_id: company_id,
                search_by: null,
                search: null,
                per_page: null,
                page: null,
            };
            const res = await configServ.getItemList(dataToSend);
            if (res.status === 200) {
                if (res.data.length > 0) {
                    const result = res.data.filter((x) => x.status === 1);
                    setProducts(result);
                }
                else {
                    setProducts([])
                }
            }
        }
        catch (error) {
            console.log(error);
        }
    };

    const fetchCostMaster = async () => {
        try {
            const res = await configServ.getAllCostMasters();
            if (res.status === 200) {
                const result = res.data.filter(x => x.status === 1);
                setCostMasterList(result || []);
            }
            else {
                setCostMasterList([]);
            }
        }
        catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        fetchAccountList();
        fetchSupplierList();
        fetchLocation();
        fetchItemList();
        fetchCostMaster();
    }, []);

    useEffect(() => {
        validateRows();
    }, [rows]);

    useEffect(() => {
        voucherDetails(rows);
    }, [rows]);

    const handleAddRow = () => {
        setRows([...rows, { ...initialRow }]);
    };

    const handleDeleteRow = (index) => {
        const newRows = rows.filter((_, i) => i !== index);
        setRows(newRows);
    };

    const handleAccountChange = async (index, field, value) => {
        const newRows = [...rows];
        newRows[index][field] = value;

        setRows(newRows);
    };

    const handleChange = async (index, field, value) => {
        const newRows = [...rows];
        newRows[index][field] = value;

        setRows(newRows);
    };

    const validateRows = () => {
        const hasError = rows.some((row) => !row.amount);
        setError(hasError);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    //image to base64 converter
    const convertToBase64 = async (file) => {
        return new Promise((resolve, reject) => {
            const fileReader = new FileReader();
            fileReader.readAsDataURL(file);
            fileReader.onload = () => {
                resolve(fileReader.result);
            };
            fileReader.onerror = (error) => {
                reject(error);
            };
        });
    };

    const handleFileUpload = async (event, index) => {
        const newRows = [...rows];
        const file = event.target.files[0];

        if (!file) return;
        const fileType = file.type;

        // Validate file type and extension
        // if (fileType.startsWith("image/")) {
        //   const allowedExtensions = [
        //     "image/jpeg",
        //     "image/png",
        //     "image/webp",
        //     "image/jpg",
        //   ];
        //   if (!allowedExtensions.includes(fileType)) {
        //     toast.error(
        //       "Only JPEG, PNG, WEBP, or JPG formats are allowed for images."
        //     );
        //     return;
        //   }
        // } else 
        if (fileType !== "application/pdf") {
            alert("Only images and PDFs are supported.");
            return;
        }

        // Check file size
        // const maxSize = file.type.startsWith("image/")
        //   ? 200 * 1024
        //   : 1 * 1024 * 1024; // 200 KB for images, 1 MB for PDFs
        // if (file.size > maxSize) {
        //   toast.error(
        //     file.type.startsWith("image/")
        //       ? "Image size should not exceed 200 KB."
        //       : "PDF size should not exceed 1 MB."
        //   );
        //   return;
        // }

        // Check file type

        // if (fileType.startsWith("image/")) {
        //   // Convert image to Base64
        //   const base64Image = await convertToBase64(file);
        //   newRows[index].image = base64Image;
        //   newRows[index].fileType = "image";
        // } else 
        if (fileType === "application/pdf") {
            // Convert PDF to Base64
            const base64Pdf = await convertToBase64(file);
            //   setBase64pdf(base64Pdf)
            //   console.log(base64Pdf); // Ensure this contains the expected Base64 format
            //   newRows[index].pdf = `data:application/pdf;base64,${base64Pdf}`; // Prepend PDF MIME type
            newRows[index].image = base64Pdf; // Prepend PDF MIME type
            newRows[index].fileType = "pdf";
        } else {
            alert("Only images and PDFs are supported.");
            return;
        }

        setRows(newRows);
    };

    const handleRemoveImage = (index) => {
        const newRows = [...rows];
        newRows[index].image = null; // Clear image/PDF URL
        newRows[index].fileType = null; // Clear file type
        setRows(newRows); // Update state
    };

    //#endregion

    return (
        <Grid container spacing={1} px={2} pb={2}>
            {rows.map((row, index) => (
                <>
                    <UploadListImage
                        open={open}
                        handleClose={handleClose}
                        index={index}
                        handleFileUpload={handleFileUpload}
                        handleRemoveImage={handleRemoveImage}
                        image={row.image || row.pdf} // Pass the correct field based on the file type
                        fileType={row.fileType} // Pass file type to determine preview logic
                    />
                    <Grid container item spacing={1} key={index}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Autocomplete
                                disablePortal
                                fullWidth
                                size="small"
                                id="account"
                                name="account"
                                options={accountList}
                                getOptionLabel={(option) => option.accountname}
                                sx={{ background: "white" }}
                                value={
                                    accountList.find(
                                        (account) => account.id === row.account
                                    ) || null
                                }
                                onChange={(event, newValue) => {
                                    if (newValue) {
                                        handleAccountChange(index, "account", newValue.id);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="standard"
                                        label="Account"
                                        error={!row.account}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Autocomplete
                                disablePortal
                                fullWidth
                                size="small"
                                id="product"
                                name="product"
                                options={products}
                                getOptionLabel={(option) => option.item_name}
                                sx={{ background: "white" }}
                                value={
                                    products.find(
                                        (account) => account.id === row.product
                                    ) || null
                                }
                                onChange={(event, newValue) => {
                                    if (newValue) {
                                        handleAccountChange(index, "product", newValue.id);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="standard"
                                        label="Product"
                                        error={!row.product}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Autocomplete
                                disablePortal
                                fullWidth
                                size="small"
                                id="costmaster"
                                name="costmaster"
                                options={costMasterList}
                                getOptionLabel={(option) => option.name}
                                sx={{ background: "white" }}
                                value={
                                    costMasterList.find(
                                        (account) => account.id === row.costmaster
                                    ) || null
                                }
                                onChange={(event, newValue) => {
                                    if (newValue) {
                                        handleAccountChange(index, "costmaster", newValue.id);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="standard"
                                        label="Cost Master"
                                        error={!row.costmaster}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Autocomplete
                                disablePortal
                                fullWidth
                                size="small"
                                id="location"
                                name="location"
                                options={locations}
                                getOptionLabel={(option) => option.location_name}
                                sx={{ background: "white" }}
                                value={
                                    locations.find(
                                        (account) => account.id === row.location
                                    ) || null
                                }
                                onChange={(event, newValue) => {
                                    if (newValue) {
                                        handleAccountChange(index, "location", newValue.id);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="standard"
                                        label="Location"
                                        error={!row.location}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small" sx={{ backgroundColor: "white" }} >
                                <InputLabel htmlFor="currency">Currency</InputLabel>
                                <Select
                                    fullWidth
                                    variant="standard"
                                    id="currency"
                                    value={row.currency || ""}
                                    name="currency"
                                    onChange={(e) => handleChange(index, "currency", e.target.value)}
                                    label="Currency"
                                    sx={{ backgroundColor: "white" }}
                                >
                                    {CurrencyList.map((item) => (
                                        <MenuItem key={item.code} value={item.code}>
                                            {item.code}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                size="small"
                                type="number"
                                variant="standard"
                                sx={{ background: "white" }}
                                label={"Amount"}
                                value={row.amount}
                                onChange={(e) => handleChange(index, "amount", e.target.value)}
                                required
                                fullWidth
                                error={!row.amount}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={1}>
                            <Stack direction={"row"}>
                                <IconButton
                                    onClick={handleAddRow}
                                    disabled={index !== rows.length - 1}
                                >
                                    <AddBoxIcon color="primary" />
                                </IconButton>
                                <IconButton
                                    onClick={() => handleOpen(index)}
                                    disabled={index !== rows.length - 1}
                                >
                                    <CameraAltIcon />
                                </IconButton>
                                <IconButton
                                    onClick={() => handleDeleteRow(index)}
                                    disabled={index === 0}
                                >
                                    <Delete color="error" />
                                </IconButton>
                            </Stack>
                        </Grid>
                    </Grid>
                </>
            ))}
        </Grid>
    );
};

export default memo(BudgetItem1);
